import { toSafeEmbedSrc } from './embed-click-to-load'

/**
 * Sanitizes the embed HTML iframely returns (stored on IFRAMELY embed elements) before it is inserted. The static
 * renderer's schema parse cannot be used: it would drop the `a[data-iframely-url]` anchor that iframely's embed.js
 * turns into the player. So this keeps a small allowlist over an inert DOMParser document instead.
 */

const KEPT_TAGS = new Set(['DIV', 'A', 'IFRAME', 'BLOCKQUOTE', 'P', 'SPAN', 'BR'])
// Removed together with their content; anything else not kept is unwrapped (its children stay)
const DROPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'OBJECT', 'EMBED', 'SVG', 'MATH'])
const PLAIN_ATTRS = new Set(['title', 'allowfullscreen', 'frameborder'])
// Kept on iframes as iframely sets them: each only restricts what the frame may do or when it loads
const IFRAME_ATTRS = new Set(['sandbox', 'referrerpolicy', 'loading'])
// Only iframely's own classes: any other could borrow an app-wide style (a fixed overlay, `.hidden`) to redress the UI
const IFRAMELY_CLASS = /^iframely-/
// Applied to an iframe stored without a `sandbox`. Keeps what players and form/booking widgets need (scripts, their own
// origin, forms, popups that open unsandboxed, fullscreen presentation); never top navigation or downloads
const DEFAULT_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation'
const HTTP_URL = /^https?:\/\//i
// Permissions-policy features for hardware and payments, which no content embed needs: never delegated to an embed
// from stored HTML. Camera, microphone, screen capture and location stay (forms, meeting widgets and maps ask for
// them, and the browser still asks the viewer), as does everything else iframely sets (autoplay, fullscreen, …).
const DENIED_FEATURES = new Set(['usb', 'serial', 'hid', 'bluetooth', 'midi', 'payment'])

/** The iframely classes of a `class` value; null when it has none. */
function filterClass(value: string): string | null {
  const kept = value.split(/\s+/).filter(token => IFRAMELY_CLASS.test(token))
  return kept.length ? kept.join(' ') : null
}

/** An iframe `allow` value without denied features; null when nothing is left. Unchanged when nothing is denied. */
function filterAllow(value: string): string | null {
  const directives = value.split(';')
  const kept = directives.filter(directive => {
    const feature = directive.trim().split(/\s+/)[0]?.toLowerCase()
    return !!feature && !DENIED_FEATURES.has(feature)
  })
  if (kept.length === directives.filter(directive => directive.trim()).length) return value
  return kept.length ? kept.map(directive => directive.trim()).join('; ') : null
}

function cleanAttributes(el: Element): void {
  Array.from(el.attributes).forEach(({ name, value }) => {
    let kept: string | null = null
    if (PLAIN_ATTRS.has(name)) kept = value
    else if (name === 'class') kept = filterClass(value)
    else if (IFRAME_ATTRS.has(name) && el.tagName === 'IFRAME') kept = value
    else if (name === 'allow' && el.tagName === 'IFRAME') kept = filterAllow(value)
    else if (name === 'href' && el.tagName === 'A') kept = HTTP_URL.test(value.trim()) ? value : null
    else if (name === 'data-iframely-url' || (name === 'src' && el.tagName === 'IFRAME')) kept = toSafeEmbedSrc(value)
    if (kept === null) el.removeAttribute(name)
    else if (kept !== value) el.setAttribute(name, kept)
  })
  if (el.tagName === 'A' && el.hasAttribute('href')) el.setAttribute('rel', 'noopener noreferrer')
}

function clean(node: Node): void {
  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) return
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.parentNode?.removeChild(child)
      return
    }
    const el = child as Element
    const tag = el.tagName.toUpperCase()
    if (DROPPED_TAGS.has(tag)) {
      el.remove()
      return
    }
    clean(el)
    if (!KEPT_TAGS.has(tag)) {
      el.replaceWith(...Array.from(el.childNodes))
      return
    }
    cleanAttributes(el)
    // iframe.ly's lazy players (`lazy=1`) leave src to embed.js and carry the player in data-iframely-url, already
    // checked above (https only). It becomes the src, so the player loads without the script - which ad blockers
    // often block - and nothing is left for the script to swap
    if (tag === 'IFRAME' && !el.hasAttribute('src') && el.hasAttribute('data-iframely-url')) {
      el.setAttribute('src', el.getAttribute('data-iframely-url')!)
      el.removeAttribute('data-iframely-url')
    }
    // An iframe without an https src can never load; drop it rather than leave an empty frame
    if (tag === 'IFRAME' && !el.hasAttribute('src')) el.remove()
    else if (tag === 'IFRAME' && !el.hasAttribute('sandbox')) el.setAttribute('sandbox', DEFAULT_IFRAME_SANDBOX)
  })
}

/** A fragment for the given document holding only allowlisted markup; never a string to assign to innerHTML. */
export function sanitizeEmbedHtml(html: string | null | undefined, target: Document = document): DocumentFragment {
  const fragment = target.createDocumentFragment()
  if (!html) return fragment
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  clean(parsed.body)
  Array.from(parsed.body.childNodes).forEach(child => fragment.appendChild(target.importNode(child, true)))
  return fragment
}

/** The page stored embed HTML shows (its link, else its iframe), for the export's print card; null if it has none. */
export function embedHtmlPageUrl(html: string | null | undefined): string | null {
  return pageUrlOf(sanitizeEmbedHtml(html))
}

/** embedHtmlPageUrl for HTML already sanitized, so a caller that also renders it parses it once */
export function pageUrlOf(fragment: DocumentFragment): string | null {
  const link = fragment.querySelector('a[href]')?.getAttribute('href')
  return link?.trim() || fragment.querySelector('iframe[src]')?.getAttribute('src') || null
}
